import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { fetchMachineTasks, listAgents } from '@/lib/agents-api';
import { useIslands } from '@/lib/islands';
import { fetchPakiety, usunPakiet, wgrajPakiet, zlecInstalacje } from '@/lib/programy-api';

/**
 * INSTALOWANIE PROGRAMÓW NA MASZYNACH.
 *
 * ⚠️ POWSTAŁO Z AWARII (2026-08-24): agent NinjaOne zniknął ze WSZYSTKICH maszyn vendingowych,
 * a nasz agent stoi na każdej — czyli byliśmy jedynym kanałem dostawy. Ekran jest jednak CELOWO
 * OGÓLNY: tak samo służy do 7-zipa, Notepada++ i czegokolwiek innego, a nazwa żadnego konkretnego
 * programu nie występuje w kodzie.
 *
 * ⚠️ INSTALACJA IDZIE NA JEDNĄ WYBRANĄ MASZYNĘ. Nie ma tu „na wszystkie" i nie ma tego przez
 * przypadek: gdyby paczka położyła maszynę, tracimy właśnie ten kanał, którym moglibyśmy naprawiać.
 */

const ROZMIAR = (b: number): string =>
  b < 1024 * 1024 ? `${Math.round(b / 1024)} kB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

export function Programy() {
  const { groupId } = useIslands();
  const toast = useToast();
  const qc = useQueryClient();
  const plikRef = useRef<HTMLInputElement>(null);

  const [nazwa, setNazwa] = useState('');
  const [parametryPaczki, setParametryPaczki] = useState('');
  const [procent, setProcent] = useState<number | null>(null);

  const [wezel, setWezel] = useState('');
  const [zrodlo, setZrodlo] = useState<'PAKIET' | 'LINK'>('PAKIET');
  const [pakietId, setPakietId] = useState('');
  const [url, setUrl] = useState('');
  const [nazwaPliku, setNazwaPliku] = useState('');
  const [sha256, setSha256] = useState('');
  const [parametry, setParametry] = useState('');
  const [kody, setKody] = useState('0, 3010');
  const [limit, setLimit] = useState('900');
  const [podpis, setPodpis] = useState(true);

  const pakiety = useQuery({
    queryKey: ['pakiety', groupId],
    queryFn: () => fetchPakiety(groupId!),
    enabled: Boolean(groupId),
  });
  const agenci = useQuery({
    queryKey: ['agents', groupId],
    queryFn: () => listAgents(groupId!),
    enabled: Boolean(groupId),
  });
  const zadania = useQuery({
    queryKey: ['machine-tasks', groupId],
    queryFn: () => fetchMachineTasks(groupId!),
    enabled: Boolean(groupId),
    refetchInterval: 5000,
  });

  const wgraj = useMutation({
    mutationFn: async (plik: File) => wgrajPakiet(groupId!, plik, { nazwa, parametry: parametryPaczki }, setProcent),
    onSuccess: (w) => {
      toast.success(w.jużByła ? `Ta paczka już była w bibliotece: „${w.nazwa}"` : `Wgrano „${w.nazwa}"`);
      setNazwa('');
      setParametryPaczki('');
      setProcent(null);
      void qc.invalidateQueries({ queryKey: ['pakiety'] });
    },
    onError: (e: Error) => {
      setProcent(null);
      toast.error(e.message);
    },
  });

  const instaluj = useMutation({
    mutationFn: () =>
      zlecInstalacje({
        groupId: groupId!,
        agentId: wezel,
        ...(zrodlo === 'PAKIET'
          ? { pakietId }
          : {
              url: url.trim(),
              nazwaPliku: nazwaPliku.trim(),
              ...(sha256.trim() === '' ? {} : { sha256: sha256.trim() }),
            }),
        ...(parametry.trim() === '' ? {} : { parametry: parametry.trim() }),
        kodySukcesu: kody
          .split(',')
          .map((x) => Number(x.trim()))
          .filter((x) => Number.isInteger(x) && x >= 0),
        limitSekund: Number(limit) || 900,
        wymagajPodpisu: podpis,
      }),
    onSuccess: (w) => {
      toast.success(`Zlecono instalację: ${w.opis}`);
      void qc.invalidateQueries({ queryKey: ['machine-tasks'] });
    },
    onError: (e: Error) => toast.error(e instanceof ApiError ? e.message : 'Nie udało się zlecić instalacji'),
  });

  if (!groupId) return <p className="text-sm text-muted-foreground">Wybierz wyspę, żeby zarządzać programami.</p>;

  const instalacje = (zadania.data ?? []).filter((z) => z.kind === 'PROGRAM_ZAINSTALUJ');
  const wybranyPakiet = pakiety.data?.pakiety.find((p) => p.id === pakietId);
  const gotowe =
    wezel !== '' && (zrodlo === 'PAKIET' ? pakietId !== '' : url.trim() !== '' && nazwaPliku.trim() !== '');

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">Programy na maszynach</h1>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Instalacja pliku <code>.msi</code> albo <code>.exe</code> na <b>wybranej</b> maszynie tej wyspy — z paczki
          wgranej tutaj albo wprost z linku. Agent sprawdza sumę kontrolną i podpis pliku przed uruchomieniem,
          a w meldunku wraca kod wyjścia instalatora.
        </p>
      </header>

      {/* ── BIBLIOTEKA PACZEK ─────────────────────────────────────────────── */}
      <section className="space-y-3 rounded-lg border border-border bg-card p-4">
        <h2 className="text-sm font-semibold">Biblioteka paczek</h2>
        <div className="flex flex-wrap items-end gap-2">
          <label className="space-y-1">
            <span className="block text-[11px] text-muted-foreground">Nazwa dla człowieka</span>
            <Input
              value={nazwa}
              onChange={(e) => setNazwa(e.target.value)}
              placeholder="np. 7-Zip 24.08 x64"
              className="h-8 w-56 text-xs"
            />
          </label>
          <label className="space-y-1">
            <span className="block text-[11px] text-muted-foreground">Domyślne parametry</span>
            <Input
              value={parametryPaczki}
              onChange={(e) => setParametryPaczki(e.target.value)}
              placeholder="/qn /norestart"
              className="h-8 w-56 text-xs"
            />
          </label>
          {/*
            WYBÓR PLIKU Z KOMPUTERA. ⚠️ UKRYTE POLE PLIKU, PRZYCISK OBOK — natywnego
            `<input type="file">` nie da się ostylować, bo przeglądarka rysuje go sama: między dwoma
            normalnymi polami stał jako goły napis „Przeglądaj...", niepodobny do niczego innego na
            ekranie. Ta sama sztuczka co przy wgrywaniu paczki na ekranie kopii.
          */}
          <input
            ref={plikRef}
            type="file"
            accept=".msi,.exe"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              /**
               * ⚠️ CZYŚCIMY POLE OD RAZU. Bez tego wybranie TEGO SAMEGO pliku drugi raz nie wywoła
               * `onChange` — czyli po nieudanym wgraniu ponowna próba tym samym plikiem wyglądałaby
               * jak martwy przycisk.
               */
              e.target.value = '';
              if (f) wgraj.mutate(f);
            }}
          />
          <Button
            variant="outline"
            size="sm"
            disabled={wgraj.isPending}
            onClick={() => plikRef.current?.click()}
            title="Wybierz instalator .msi albo .exe z tego komputera — wgra się od razu"
          >
            {wgraj.isPending ? <Loader2 className="animate-spin" /> : <Upload />}
            {wgraj.isPending ? `wgrywam… ${procent ?? 0}%` : 'Wgraj instalator'}
          </Button>
        </div>

        {pakiety.isPending ? (
          <p className="text-sm text-muted-foreground">
            <Loader2 className="mr-1 inline size-3 animate-spin" />
            wczytuję…
          </p>
        ) : (pakiety.data?.pakiety.length ?? 0) === 0 ? (
          <p className="text-sm text-muted-foreground">Biblioteka jest pusta — wgraj pierwszy instalator.</p>
        ) : (
          <div className="overflow-x-auto rounded border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="p-2">Nazwa</th>
                  <th className="p-2">Plik</th>
                  <th className="p-2 text-right">Rozmiar</th>
                  <th className="p-2">Parametry</th>
                  <th className="p-2">SHA-256</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {pakiety.data!.pakiety.map((p) => (
                  <tr key={p.id} className="border-t">
                    <td className="p-2">{p.nazwa}</td>
                    <td className="p-2 font-mono text-[11px]">{p.plik}</td>
                    <td className="p-2 text-right tabular-nums text-[12px]">{ROZMIAR(p.rozmiar)}</td>
                    <td className="p-2 font-mono text-[11px]">{p.parametry ?? '—'}</td>
                    <td className="p-2 font-mono text-[10px]" title={p.sha256}>
                      {p.sha256.slice(0, 12)}…
                    </td>
                    <td className="p-2 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7"
                        onClick={() => {
                          void usunPakiet(groupId, p.id)
                            .then(() => qc.invalidateQueries({ queryKey: ['pakiety'] }))
                            .catch((e: Error) => toast.error(e.message));
                        }}
                      >
                        <Trash2 className="size-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── ZLECENIE INSTALACJI ───────────────────────────────────────────── */}
      <section className="space-y-3 rounded-lg border border-border bg-card p-4">
        <h2 className="text-sm font-semibold">Zainstaluj na maszynie</h2>

        <div className="flex flex-wrap items-end gap-2">
          <label className="space-y-1">
            <span className="block text-[11px] text-muted-foreground">Maszyna</span>
            <Select value={wezel} onChange={(e) => setWezel(e.target.value)} className="h-8 w-72 text-xs">
              <option value="">— wybierz —</option>
              {(agenci.data?.agents ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nodeId}
                  {a.hostname !== null && a.hostname !== a.nodeId ? ` (${a.hostname})` : ''}
                  {a.acceptance === 'POTWIERDZONY' ? '' : ` — ${a.acceptance}`}
                </option>
              ))}
            </Select>
          </label>

          <label className="space-y-1">
            <span className="block text-[11px] text-muted-foreground">Skąd plik</span>
            <Select
              value={zrodlo}
              onChange={(e) => setZrodlo(e.target.value as 'PAKIET' | 'LINK')}
              className="h-8 w-40 text-xs"
            >
              <option value="PAKIET">z biblioteki</option>
              <option value="LINK">z linku</option>
            </Select>
          </label>

          {zrodlo === 'PAKIET' ? (
            <label className="space-y-1">
              <span className="block text-[11px] text-muted-foreground">Paczka</span>
              <Select
                value={pakietId}
                onChange={(e) => {
                  setPakietId(e.target.value);
                  const p = pakiety.data?.pakiety.find((x) => x.id === e.target.value);
                  if (p?.parametry) setParametry(p.parametry);
                }}
                className="h-8 w-72 text-xs"
              >
                <option value="">— wybierz —</option>
                {(pakiety.data?.pakiety ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nazwa} ({p.plik})
                  </option>
                ))}
              </Select>
            </label>
          ) : (
            <>
              <label className="space-y-1">
                <span className="block text-[11px] text-muted-foreground">Link (https)</span>
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://…/7z2408-x64.msi"
                  className="h-8 w-80 text-xs"
                />
              </label>
              <label className="space-y-1">
                <span className="block text-[11px] text-muted-foreground">Nazwa pliku</span>
                <Input
                  value={nazwaPliku}
                  onChange={(e) => setNazwaPliku(e.target.value)}
                  placeholder="7z2408-x64.msi"
                  className="h-8 w-48 text-xs"
                />
              </label>
              <label className="space-y-1">
                <span className="block text-[11px] text-muted-foreground">SHA-256 (zalecane)</span>
                <Input
                  value={sha256}
                  onChange={(e) => setSha256(e.target.value)}
                  placeholder="64 znaki"
                  className="h-8 w-56 font-mono text-[11px]"
                />
              </label>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <label className="space-y-1">
            <span className="block text-[11px] text-muted-foreground">Parametry instalacji</span>
            <Input
              value={parametry}
              onChange={(e) => setParametry(e.target.value)}
              placeholder="/qn /norestart"
              className="h-8 w-80 text-xs"
            />
          </label>
          <label className="space-y-1">
            <span className="block text-[11px] text-muted-foreground">Kody sukcesu</span>
            <Input value={kody} onChange={(e) => setKody(e.target.value)} className="h-8 w-32 text-xs" />
          </label>
          <label className="space-y-1">
            <span className="block text-[11px] text-muted-foreground">Limit czasu (s)</span>
            <Input value={limit} onChange={(e) => setLimit(e.target.value)} className="h-8 w-24 text-xs" />
          </label>
          <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <input type="checkbox" checked={podpis} onChange={(e) => setPodpis(e.target.checked)} />
            wymagaj ważnego podpisu pliku
          </label>
          <Button size="sm" className="h-8" disabled={!gotowe || instaluj.isPending} onClick={() => instaluj.mutate()}>
            {instaluj.isPending && <Loader2 className="mr-1 size-3 animate-spin" />}
            Zainstaluj
          </Button>
        </div>

        <p className="max-w-3xl text-[11px] text-muted-foreground">
          {/*
            ⚠️ Trzy zdania, które muszą tu być, bo to zdalne uruchomienie kodu u klienta.
          */}
          Agent pobierze plik, sprawdzi <b>sumę kontrolną</b> i <b>podpis</b>, a potem uruchomi go z podanymi
          parametrami — <code>.msi</code> przez <code>msiexec /i</code>, <code>.exe</code> wprost.{' '}
          <b>Kod 3010</b> znaczy „zainstalowano, wymagany restart" i dlatego domyślnie jest na liście sukcesów.
          Przy linku bez sumy kontrolnej wymóg podpisu zostaje włączony niezależnie od tego pola — wtedy podpis
          jest jedynym sprawdzeniem treści, jakie mamy.{' '}
          {/*
            ⚠️ ZDANIE O LOGU POWSTAŁO Z POMIARU, nie z ostrożności. Udana instalacja VC++ na małpkomacie
            (25.08.2026) oddała `wyjscie: ""` — instalator okienkowy nie pisze na `stdout` ani `stderr`,
            więc po NIEUDANEJ instalacji w portalu zostaje sam kod wyjścia i nic więcej. Podpowiedź o `/l*v`
            jest jedyną drogą, żeby człowiek miał co czytać, a musi być TU, bo tam się wpisuje parametry.
          */}
          Instalator w trybie cichym <b>nic nie pisze na wyjściu</b> — po nieudanej instalacji zostaje sam kod.
          Jeśli chcesz więcej, dopisz do parametrów MSI <code>/l*v C:\Windows\Temp\instalacja.log</code> i zajrzyj
          do tego pliku na maszynie.
          {wybranyPakiet && (
            <>
              {' '}
              Wybrana paczka: <code>{wybranyPakiet.plik}</code>, {ROZMIAR(wybranyPakiet.rozmiar)}, suma{' '}
              <code>{wybranyPakiet.sha256.slice(0, 16)}…</code>.
            </>
          )}
        </p>
      </section>

      {/* ── OSTATNIE INSTALACJE ───────────────────────────────────────────── */}
      <section className="space-y-2 rounded-lg border border-border bg-card p-4">
        <h2 className="text-sm font-semibold">Ostatnie instalacje</h2>
        {instalacje.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nic jeszcze nie zlecono na tej wyspie.</p>
        ) : (
          <div className="overflow-x-auto rounded border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="p-2">Kiedy</th>
                  <th className="p-2">Maszyna</th>
                  <th className="p-2">Stan</th>
                  <th className="p-2">Wynik</th>
                </tr>
              </thead>
              <tbody>
                {instalacje.map((z) => (
                  <tr key={z.id} className="border-t align-top">
                    <td className="p-2 whitespace-nowrap text-[12px]">{new Date(z.createdAt).toLocaleString('pl')}</td>
                    <td className="p-2 text-[12px]">{z.agent?.nodeId ?? '—'}</td>
                    <td className="p-2">
                      <Badge variant={z.status === 'DONE' ? 'success' : z.status === 'FAILED' ? 'destructive' : 'neutral'}>
                        {z.status}
                      </Badge>
                      {z.note !== null && z.note !== '' && (
                        <span className="ml-1 text-[11px] text-muted-foreground">{z.note}</span>
                      )}
                    </td>
                    <td className="p-2 text-[11px]">
                      {z.error !== null && z.error !== '' ? (
                        <span className="text-[var(--warning)]">{z.error}</span>
                      ) : (
                        <WynikInstalacji output={z.output} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * Wynik instalacji tak, jak go zwrócił agent. ⚠️ Kod wyjścia i podpis pokazujemy ZAWSZE, gdy są —
 * „zrobione" bez kodu nie jest przy stu pięćdziesięciu maszynach niczym do rozliczenia.
 */
function WynikInstalacji({ output }: { output: unknown }) {
  if (output === null || typeof output !== 'object') return <span className="text-muted-foreground">—</span>;
  const o = output as { kod?: number; podpis?: string; sekundy?: number; wyjscie?: string };
  return (
    <span>
      kod <b>{o.kod ?? '—'}</b>
      {o.podpis !== undefined ? `, podpis: ${o.podpis}` : ''}
      {o.sekundy !== undefined ? `, ${o.sekundy} s` : ''}
      {o.wyjscie !== undefined && o.wyjscie.trim() !== '' && (
        <pre className="mt-1 max-h-32 max-w-[40rem] overflow-auto rounded border bg-muted/30 p-1 text-[10px] whitespace-pre-wrap">
          {o.wyjscie}
        </pre>
      )}
    </span>
  );
}
